import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, Redirect, useParams } from 'react-router-dom';
import Decimal from 'decimal.js';

import BlockUi from 'react-block-ui';
import { GridLoader } from 'react-spinners';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useHistory } from 'react-router';
import qs from 'qs';

import { getAccountDetailsApi } from '../../../../api/order';
import logo from '../../../../assets/images/logo_new.png'
import useAuthDataStore from '../../../../hooks/store/useAuthDataStore';
import { getRetailerBillingAddressSvc } from '../../../../services/retailer';
import { getOrderDetailApi } from '../../../../api/order';
import { showCustomToast } from '../../../../controls/CustomToast';
import { formatDate } from '../../../../utils/formatDate';
import './OrderSummaryPage.scss';

const OrderSummaryPage = (props) => {
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();
    const { retailerId } = useAuthDataStore.getState();
    const [orderDetail, setOrderDetail] = useState(null);
    const [billingInfo, setBillingInfo] = useState(null);

    const { orderId } = useParams();

    useEffect(() => {
        if (orderId) {
            getOrderDetailApi({ id: orderId })
                .then((res) => {
                    const detail = Array.isArray(res.result)
                        ? res.result[0]
                        : res.result;
                    setOrderDetail(detail);
                })

            getRetailerBillingAddressSvc(retailerId)
                .then((res) => {
                    if(res.success) {
                        setBillingInfo(res.data);
                    }else{
                        showCustomToast(
                            'Error, Please try again later.'
                          );
                    }
                }).catch((err) => {
                    showCustomToast(
                        'Error, Please try again later.'
                      );
                });
        }
    }, [orderId, retailerId]);

    useEffect(() => {
        if (orderDetail && billingInfo) {
            requestAnimationFrame(() => {
                setTimeout(() => {
                    window.print();
                }, 500);
            });
        }
    }, [orderDetail, billingInfo]);

    const formatCurrency = (amount) => {
        const decimalAmount = new Decimal(amount);
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD'
        }).format(decimalAmount.toFixed(2));
    };

    const formatPaymentMethod = (paymentType, paymentGateway, paymentMethod) => {
        const paymentMethodMap = {
            1: 'Wallet',
            2: 'Credit Card',
            3: 'PayPal',
            4: 'Flex Payment'
        };
        const paymentGatewayMap = {
            "PAYPAL": 'PayPal',
            "CARD": 'Credit Card',
        };
        const result = paymentMethodMap[paymentType] || paymentMethod;
        if (paymentType === 3) {
            return paymentGatewayMap[paymentGateway] || result;
        } else {
            return result
        }
    };

    if (!orderDetail || !billingInfo || orderDetail.status === 'unpaid' || orderDetail.status === 'cancelled') {
        return null;
    }
    return (
        <div className="order-summary-page">
            {/* Order Header with Logo */}
            <div className="order-header">
                <div className="order-info">
                    <p className='dsz-order-id'>Dropshipzone Order ID {orderDetail.increment_id}</p>
                    {orderDetail.original_customer_order_id && <p className='your-order-id'>Your Order ID: <span className='your-order-number'>{orderDetail.original_customer_order_id}</span></p>}
                    <p className="your-order-id">Order Date: <span className='your-order-number'>{formatDate(orderDetail.order_date)}</span></p>
                </div>
                <div className="header-logo">
                    <img
                        src={logo}
                        className="logo"
                        alt="Dropshipzone"
                    />
                </div>
            </div>
            <div className="order-summary-content">
                {/* Print Head - removed logo */}
                <div className="print-head">
                </div>

                {/* Two Column Layout - Addresses */}
                <div className="col2-set">
                    <div className="column-1">
                        <div className='detail-header'>Shipping Address</div>
                        <address>
                            {orderDetail.first_name + ' ' + orderDetail.last_name}<br />
                            {orderDetail.address_line1}<br />
                            {orderDetail.address_line2 && (
                                <>
                                    {orderDetail.address_line2}<br />
                                </>
                            )}
                            {orderDetail.suburb +
                                ' ' +
                                orderDetail.state +
                                ' ' +
                                orderDetail.postcode}<br />
                            T: {orderDetail.phone}
                        </address>
                    </div>
                    <div className="column-2">
                        <div className='detail-header'>Billing Address</div>
                        <address>
                            {billingInfo.firstname + ' ' + billingInfo.lastname}<br />
                            {billingInfo.street}<br />
                            {billingInfo.suburb +
                                ' ' +
                                billingInfo.state +
                                ' ' +
                                billingInfo.postcode}<br />
                            T: {billingInfo.telephone}<br />
                        </address>
                    </div>
                </div>

                {/* Two Column Layout - Shipping & Payment */}
                <div className="col2-set">
                    {orderDetail.shipments &&
                        (<div className="column-1">
                            <div className='detail-header'>Shipping Method</div>
                            {orderDetail.shipments.map((item, index) => (
                                <div key={index}>
                                    <span className='detail-content'>{item.courier}</span><br />
                                    <span className='detail-content'>Tracking ID: {item.tracking_number}</span>
                                </div>
                            ))}
                        </div>)}

                    <div className="column-2">
                        <div className='detail-header'>Payment Method</div>
                        <p className='detail-content'>{formatPaymentMethod(orderDetail.payment_type, orderDetail.payment_gateway, orderDetail.payment_method)}</p>
                    </div>
                </div>

                {orderDetail.comment && <div className="col2-set">
                    <div className="column-1">
                        <div className='detail-header'>Delivery Note</div>
                        <p className='detail-content'>{orderDetail.comment}</p>
                    </div>
                </div>}

                {/* Items Ordered */}
                <p className='item-order-header'>Items Ordered</p>
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Product Name</th>
                                <th>SKU</th>
                                <th className="a-right">Unit Price</th>
                                <th className="a-right">Shipping Per Item</th>
                                <th className="a-right">Qty</th>
                                <th className="a-right">Subtotal</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orderDetail.items.map((item, index) => (
                                <tr key={index} className="order-item-row">
                                    <td className="product-name-cell">
                                        <span className="item-data-text product-name-text">{item.name}</span>
                                    </td>
                                    <td className="a-left">
                                        <span className="item-data-text">{item.sku}</span>
                                    </td>
                                    <td className="a-right">
                                        <span className="item-data-text">{formatCurrency(item.price)}</span>
                                    </td>
                                    <td className="a-right">
                                        <span className="item-data-text">{formatCurrency(item.shipping_fee)}</span>
                                    </td>
                                    <td className="a-right">
                                        <span className="item-data-text">{item.qty}</span>
                                    </td>
                                    <td className="a-right">
                                        <span className="item-data-text">{formatCurrency(item.sub_total_inc_shipping)}</span>
                                    </td>
                                </tr>
                            ))}
                            <tr className="price-summary-text">
                                <td></td>
                                <td></td>
                                <td></td>
                                <td className="a-left nobr">Subtotal</td>
                                <td></td>
                                <td className="a-right">{formatCurrency(orderDetail.total_amount)}</td>
                            </tr>
                            <tr className="price-summary-text">
                                <td></td>
                                <td></td>
                                <td></td>
                                <td className="a-left nobr">Processing Fee</td>
                                <td></td>
                                <td className="a-right">{formatCurrency(orderDetail.processing_fee)}</td>
                            </tr>
                            <tr className="price-summary-text">
                                <td></td>
                                <td></td>
                                <td></td>
                                <td className="a-left nobr">Total</td>
                                <td></td>
                                <td className="a-right item-data-text">{formatCurrency(new Decimal(orderDetail.total_amount).plus(orderDetail.processing_fee))}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className="page-break-before-totals"></div>

                {/* Action Buttons */}
                <div className="note-text">*This document is an Order Summary only and is not a valid Tax Invoice.</div>
                <div className="buttons-set">
                    {/* <button
                        type="button"
                        title="Print Order"
                        className="button print-button"
                        onClick={() => window.print()}
                    >
                        <span>Print Order</span>
                    </button> */}
                    <button
                        type="button"
                        title="Close Window"
                        className="button close-button"
                        onClick={() => window.close()}
                    >
                        <span>Close Window</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OrderSummaryPage;
